import React, { useEffect } from "react";
import { MessageSquare, Zap, Layers, Smartphone, Shield } from "lucide-react";

import { ProjectHero } from "../ui/ProjectHero/ProjectHero";
import { VisualShowcase } from "../ui/VisualShowcase/VisualShowcase";
import { VisualLottieShowcase } from "../ui/VisualLottieShowcase/VisualLottieShowcase";
import { ProjectOverview } from "../ui/ProjectOverview/ProjectOverview";
import { BulletItem, BulletList } from "../ui/Bullets/Bullets";
import { ProcessSection } from "../ui/ProcessSection/ProcessSection";
import { KeyFeatures } from "../ui/KeyFeatures/KeyFeatures";
import { ProjectFooter } from "../ui/ProjectFooter/ProjectFooter";
import Footer from "../../../container/InlineFooter/InlineFooter";

import images from "../../../constants/images";
import chapterAnimation1 from "../../../assets/lottie-animations/chapter_animation.json";

import "./Chapter.scss";

const Chapter = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  const bulletListLeft = (
    <BulletList>
      <BulletItem>
        Supporting natural language queries via text and voice
      </BulletItem>
      <BulletItem>
        Delivering rich, structured responses (text, images, PDFs, video)
      </BulletItem>
      <BulletItem>
        Streaming responses in real time without blocking or degrading the
        experience
      </BulletItem>
      <BulletItem>
        Designing an interface that feels trustworthy, calm, and usable in
        professional contexts
      </BulletItem>
      <BulletItem>
        Doing all of this in a mobile-first environment, using web technologies
        rather than native platforms
      </BulletItem>
    </BulletList>
  );

  const bulletsListRight = (
    <BulletList>
      <BulletItem>Ask questions via voice or text</BulletItem>
      <BulletItem>Ask questions via voice or text</BulletItem>
      <BulletItem>
        Consume knowledge across multiple formats, including:
        <BulletList>
          <BulletItem>Text explanations</BulletItem>
          <BulletItem>Images and diagrams</BulletItem>
          <BulletItem>PDF documentation</BulletItem>
          <BulletItem>YouTube video content</BulletItem>
        </BulletList>
      </BulletItem>
    </BulletList>
  );

  return (
    <div className="main-project-container">
      <ProjectHero
        title="Building AI Chatbots for Knowledge Tooling"
        subtitle="Making complex technical knowledge instantly accessible through AI-powered, mobile-first interfaces"
        category="ENGINEERING"
        role="Lead Frontend Engineer — AI Chat Interfaces"
        year="2025"
        platform="Mobile App (iOS & Android via Web Technologies)"
        technologies={[
          "Streaming API backed by OpenAI",
          " React.js",
          " Ionic",
          " Capacitor",
        ]}
        client="Chapter Works"
      />
      <VisualShowcase
        imageUrl={images.chapterAppPallette}
        imageAlt="Chapter works image pallette"
        layout="full"
      />
      <ProjectOverview
        challengeContent={[
          "Making critical technical knowledge instantly accessible",
          "Professionals in the energy sector often need answers in the moment — on-site, under time pressure, and sometimes in safety-critical situations. Traditional documentation and training materials are slow to navigate, difficult to search, and poorly suited to mobile use.",
          "The challenge was not simply building a chatbot, but:",
        ]}
        bulletsLeft={bulletListLeft}
        solutionContent={[
          "An AI-powered, mobile-first knowledge interface",
          "The result was a mobile-first AI chatbot interface designed to make complex technical knowledge immediately accessible, usable, and scalable.",
          "The application allows users to:",
        ]}
        extraSolutionContent={[
          "Rather than overwhelming users with raw AI output, the interface focuses on clarity, pacing, and structured presentation — ensuring information can be understood and applied quickly in real-world situations.",
        ]}
        bulletsRight={bulletsListRight}
        metrics={[
          {
            value: "70%+",
            label: "Faster access to critical technical procedures",
          },
          {
            value: "<2s",
            label: "Average AI response time in-field",
          },
          {
            value: "24/7",
            label: "High-availability cloud-native infrastructure",
          },
          {
            value: "Multi-Site",
            label: "Deployed across operational energy locations",
          },
          {
            value: "Mobile-Optimized",
            label: "Designed for on-site and safety-critical workflows",
          },
        ]}
      />
      <VisualLottieShowcase
        animationOne={chapterAnimation1}
        backgroundColor="black"
        layout="full"
        title="A Production-Ready AI Knowledge Platform"
        description="Streaming AI responses, structured multi-format outputs, and a resilient mobile-first architecture — built to support real-world, safety-conscious environments at scale."
        descriptionColor="#9ca3af"
        titleColor="#ffffff"
      />

      <KeyFeatures
        title="AI Knowledge Tooling for Real Work"
        description="Features designed to support clarity, trust, and real-world usability."
        features={[
          {
            icon: MessageSquare,
            title: "Conversational knowledge access",
            description:
              "Ask questions naturally using text or voice, without needing to search through manuals or documentation.",
          },
          {
            icon: Zap,
            title: "Streamed AI responses",
            description:
              "Responses are streamed progressively, improving perceived speed and reducing cognitive load.",
          },
          {
            icon: Layers,
            title: "Multi-format knowledge delivery",
            description:
              "Combines text, images, PDF documents, and video content in a single conversational flow.",
          },
          {
            icon: Smartphone,
            title: "Mobile-first interaction design",
            description:
              "Designed specifically for professionals working on-site or on the move.",
          },
          {
            icon: Shield,
            title: "Structured, trustworthy output",
            description:
              "AI responses are presented clearly and predictably to support safe and confident decision-making.",
          },
        ]}
      />
      <ProcessSection
        title="Designing a Reliable AI Knowledge Interface"
        description="A product and engineering approach focused on clarity, trust, and usability in real-world professional contexts."
        phases={[
          {
            number: "01",
            title: "Problem Framing & Knowledge Needs",
            description:
              "Mapped how professionals search for, interpret, and apply technical knowledge, identifying where AI could reduce friction without introducing risk.",
            deliverables: [
              "User intent mapping",
              "Knowledge access pain points",
              "Voice vs text usage scenarios",
              "Success and constraint definition",
            ],
          },
          {
            number: "02",
            title: "Interaction & Experience Design",
            description:
              "Designed a chat-based interface capable of handling streamed responses and mixed content formats without overwhelming users.",
            deliverables: [
              "Chat UI patterns",
              "Streaming response behaviour",
              "Content hierarchy and pacing",
              "Mobile-first interaction flows",
            ],
          },
          {
            number: "03",
            title: "Frontend Implementation & Integration",
            description:
              "Built the application using React, Ionic, and Capacitor, integrating with a pre-built AI API to consume streamed responses efficiently.",
            deliverables: [
              "Frontend architecture",
              "Streaming API integration",
              "Voice and text input handling",
              "Performance optimisation",
            ],
          },
          {
            number: "04",
            title: "Validation & Real-World Readiness",
            description:
              "Validated usability, stability, and performance to ensure the tool could be trusted in professional and safety-conscious environments.",
            deliverables: [
              "End-to-end testing",
              "Mobile performance checks",
              "Error and edge-case handling",
              "Iteration based on feedback",
            ],
          },
        ]}
      />
      <ProjectFooter
        impactTitle="Practical AI knowledge tooling for a fast-growing industry"
        impactLabel="Project Impact"
        impactDescription="ChapterWorks gained a scalable, mobile-first AI interface that enables professionals to access complex technical knowledge quickly and confidently. The project demonstrated how AI can support real work when it’s carefully designed around clarity, structure, and context — not novelty."
        nextProject={{ title: "Babbel Subscriptions", link: "/project/babbel" }}
        conclusion={{
          label: "Design / Engineering Reflection",
          title: "AI is only useful when it earns trust",
          description:
            "Building AI-powered interfaces isn’t about showcasing intelligence — it’s about making knowledge dependable in the moments that matter. This project reinforced the importance of structured output, progressive disclosure, and mobile-first design when working with streamed AI responses in professional environments.",
        }}
      />
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Footer />
      </div>
    </div>
  );
};

export { Chapter };

/*

CLIENT AND INTRO

Building AI Chatbots for Knowledge Tooling
Project Overview

Client
ChapterWorks

Industry
Energy · Sustainability · Knowledge Management

Platform
Mobile App (iOS & Android via Web Technologies)

Year
2023

Introduction

ChapterWorks is committed to shaping the future of energy by enabling a world that is CO₂-neutral, safer, and more sustainable. Their AI-first knowledge management platform equips professionals in the energy sector with fast, reliable access to practical knowledge — from heat pumps to photovoltaic systems — ensuring teams can work safely, efficiently, and compliantly in a rapidly evolving industry.

As demand for skilled professionals grows, ChapterWorks focuses on increasing productivity and capacity through scalable, accessible knowledge tooling designed for real-world use.
*/
